import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthSuccessPlan {
  id: string;
  partyId: string;
  name: string;
  status: string;
  healthScore: number | null;
  goalsJson: string;
  milestonesJson: string;
  ownerUserId: string | null;
  openCaseRefsJson: string;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface GrowthRetentionPlay {
  id: string;
  partyId: string;
  successPlanId: string | null;
  triggerType: string;
  triggerRef: string | null;
  playbookJson: string;
  approvalState: string;
  consentChecked: boolean;
  consentBlockReason: string | null;
  channelHint: string;
  purposeKey: string;
  correlationId: string | null;
  approvalReason?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface GrowthComplaintCaseLink {
  id: string;
  partyId: string;
  externalTicketId: string;
  ticketSystemKey: string;
  ncxInteractionId: string | null;
  statusSnapshot: string;
  boundary: string;
  disclaimer: string;
  summary: string | null;
  linkedAtUtc: string;
}

export interface GrowthServiceHealth {
  partyId: string;
  clv: number | null;
  churnRisk: number | null;
  churnRiskBand: string | null;
  healthScore: number | null;
  activeSuccessPlan: GrowthSuccessPlan | null;
  openCaseLinks: GrowthComplaintCaseLink[];
  openRetentionPlays: GrowthRetentionPlay[];
  disclaimer: string;
}

export interface GrowthExpansionSignal {
  id: string;
  partyId: string;
  signalType: string;
  payloadJson: string;
  salesHandoffStatus: string;
  idempotencyKey: string;
  salesHandoffId: string | null;
  handoffResultJson: string | null;
  createdAtUtc: string;
}

export interface GrowthServiceFeedback {
  id: string;
  partyId: string;
  scoreType: string;
  score: number;
  comment: string | null;
  sourceRef: string | null;
  capturedAtUtc: string;
}

export interface CreateExpansionSignalResult {
  isReplay: boolean;
  signal: GrowthExpansionSignal;
}

export interface SuggestRetentionFromChurnResult {
  created?: boolean;
  reason?: string;
  id?: string;
  partyId?: string;
  triggerType?: string;
  approvalState?: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthServiceService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/service`;

  listSuccessPlans(partyId?: string): Observable<GrowthSuccessPlan[]> {
    let params = new HttpParams();
    if (partyId) params = params.set('partyId', partyId);
    return this.http.get<GrowthSuccessPlan[]>(`${this.base}/success-plans`, { params });
  }

  getSuccessPlan(id: string): Observable<GrowthSuccessPlan> {
    return this.http.get<GrowthSuccessPlan>(`${this.base}/success-plans/${id}`);
  }

  createSuccessPlan(payload: {
    partyId: string;
    name: string;
    goalsJson?: string | null;
    milestonesJson?: string | null;
    ownerUserId?: string | null;
  }): Observable<GrowthSuccessPlan> {
    return this.http.post<GrowthSuccessPlan>(`${this.base}/success-plans`, payload);
  }

  updateSuccessPlan(id: string, payload: {
    name: string;
    status: string;
    goalsJson?: string | null;
    milestonesJson?: string | null;
    ownerUserId?: string | null;
    openCaseRefsJson?: string | null;
  }): Observable<GrowthSuccessPlan> {
    return this.http.put<GrowthSuccessPlan>(`${this.base}/success-plans/${id}`, payload);
  }

  listRetentionPlays(partyId?: string, approvalState?: string): Observable<GrowthRetentionPlay[]> {
    let params = new HttpParams();
    if (partyId) params = params.set('partyId', partyId);
    if (approvalState) params = params.set('approvalState', approvalState);
    return this.http.get<GrowthRetentionPlay[]>(`${this.base}/retention-plays`, { params });
  }

  getRetentionPlay(id: string): Observable<GrowthRetentionPlay> {
    return this.http.get<GrowthRetentionPlay>(`${this.base}/retention-plays/${id}`);
  }

  createRetentionPlay(payload: {
    partyId: string;
    successPlanId?: string | null;
    triggerType?: string | null;
    triggerRef?: string | null;
    playbookJson?: string | null;
    channelHint?: string | null;
    correlationId?: string | null;
  }): Observable<GrowthRetentionPlay> {
    return this.http.post<GrowthRetentionPlay>(`${this.base}/retention-plays`, payload);
  }

  approveRetentionPlay(id: string, approve: boolean, reason?: string | null): Observable<GrowthRetentionPlay> {
    return this.http.post<GrowthRetentionPlay>(`${this.base}/retention-plays/${id}/approve`, { approve, reason: reason ?? null });
  }

  suggestRetentionFromChurn(partyId: string): Observable<GrowthRetentionPlay | SuggestRetentionFromChurnResult> {
    return this.http.post<GrowthRetentionPlay | SuggestRetentionFromChurnResult>(
      `${this.base}/retention-plays/suggest-from-churn/${partyId}`,
      {}
    );
  }

  getHealth(partyId: string): Observable<GrowthServiceHealth> {
    return this.http.get<GrowthServiceHealth>(`${this.base}/health/${partyId}`);
  }

  createCaseLink(payload: {
    partyId: string;
    externalTicketId: string;
    ticketSystemKey: string;
    ncxInteractionId?: string | null;
  }): Observable<GrowthComplaintCaseLink> {
    return this.http.post<GrowthComplaintCaseLink>(`${this.base}/case-links`, payload);
  }

  createExpansionSignal(payload: {
    partyId: string;
    signalType: string;
    payloadJson?: string | null;
    idempotencyKey: string;
    correlationId?: string | null;
  }): Observable<CreateExpansionSignalResult> {
    return this.http.post<CreateExpansionSignalResult>(`${this.base}/expansion-signals`, payload);
  }

  createFeedback(payload: {
    partyId: string;
    scoreType: string;
    score: number;
    comment?: string | null;
    sourceRef?: string | null;
  }): Observable<GrowthServiceFeedback> {
    return this.http.post<GrowthServiceFeedback>(`${this.base}/feedback`, payload);
  }
}
